/**
 * EL RASTRO DESDE LA PANTALLA (27-09). La regla entera, en `backend/core/rastro.py`.
 *
 * El servidor ya apunta lo que rechaza. Lo que nunca le llega hay que mandarlo desde aquí:
 * - los avisos de error que ve el cliente (todos los `toast.error` y `toast.warning`, sin
 *   tocar pantalla por pantalla: `vigilarAvisos`);
 * - las pantallas rotas (`LimiteDeError`) y los errores de JavaScript sueltos;
 * - las peticiones que ni llegaron (sin conexión, servidor caído);
 * - los frenos: la pantalla decide no mandar algo (p. ej. Guardar sin cambios).
 *
 * Nunca rompe nada ni enseña nada: si falla, se calla. Y lleva freno propio para que un
 * bucle de la pantalla no mande cien avisos iguales.
 */

// En dev y prod va VACÍA: la app y la API comparten dominio y la ruta queda relativa, igual
// que en AuthContext. No es «sin servidor»: con `!BACKEND_URL` el rastro no salía nunca.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const REPETIDO_MS = 30 * 1000;   // el mismo aviso dentro de 30 s no se repite
export const TOPE_POR_MINUTO = 20;

// Ruido del navegador que no dice nada de la app.
const RUIDO = [/ResizeObserver loop/i, /^Script error\.?$/i, /extension:\/\//i, /Non-Error promise rejection/i];

export const esRuido = (mensaje) => RUIDO.some((r) => r.test(String(mensaje || '')));

/** ¿Se manda este evento? `historial` es [{clave, t}] de lo ya mandado. Puro. */
export const debeMandarse = (historial, clave, ahora) => {
    const reciente = historial.filter((h) => ahora - h.t < 60 * 1000);
    if (reciente.length >= TOPE_POR_MINUTO) return false;
    return !reciente.some((h) => h.clave === clave && ahora - h.t < REPETIDO_MS);
};

let historial = [];

export function anotar({ tipo, mensaje, detalle } = {}) {
    try {
        const token = localStorage.getItem('token');
        if (!token || esRuido(mensaje)) return;
        const texto = String(mensaje || '').slice(0, 300);
        const clave = `${tipo}|${texto}`;
        const ahora = Date.now();
        if (!debeMandarse(historial, clave, ahora)) return;
        historial = [...historial.filter((h) => ahora - h.t < 60 * 1000), { clave, t: ahora }];
        fetch(`${BACKEND_URL}/api/rastro`, {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
                tipo,
                mensaje: texto,
                // Solo la ruta: la query puede llevar el token de recuperar la contraseña.
                pantalla: window.location.pathname.slice(0, 200),
                detalle,
            }),
        }).catch(() => {});
    } catch {
        // El rastro nunca rompe la pantalla.
    }
}

const textoDe = (m) => {
    if (typeof m === 'string') return m;
    if (m && typeof m === 'object' && typeof m.message === 'string') return m.message;
    return '';
};

let vigilando = false;

/** Envuelve `toast.error` y `toast.warning` una sola vez para apuntar lo que ve el cliente. */
export function vigilarAvisos(toast) {
    if (vigilando || !toast) return;
    vigilando = true;
    ['error', 'warning'].forEach((nivel) => {
        const original = toast[nivel];
        if (typeof original !== 'function') return;
        toast[nivel] = (mensaje, opciones) => {
            const texto = textoDe(mensaje);
            if (texto) anotar({ tipo: 'aviso_de_error', mensaje: texto, detalle: { nivel } });
            return original(mensaje, opciones);
        };
    });
}

let escuchando = false;

/** Errores de JavaScript que no recoge nadie. */
export function escucharErrores() {
    if (escuchando || typeof window === 'undefined') return;
    escuchando = true;
    window.addEventListener('error', (e) => {
        anotar({
            tipo: 'error_js',
            mensaje: e?.message,
            detalle: { fichero: e?.filename, linea: e?.lineno, columna: e?.colno },
        });
    });
    window.addEventListener('unhandledrejection', (e) => {
        const r = e?.reason;
        // Las peticiones que fallan ya las apunta el servidor o el interceptor.
        if (r?.isAxiosError) return;
        anotar({ tipo: 'error_js', mensaje: textoDe(r) || String(r || ''), detalle: { promesa: true } });
    });
}
